import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { notification } from "antd";
import { CapabilityUnavailableError, SessionExpiredError } from "@/api";

/**
 * The Studio's QueryClient. Retry rules match the dashboard's
 * (shared/config/queryClient.ts) — 401 is the interceptor's job, 403/404/440
 * won't improve with retries. The error surface is deliberately different:
 * no handleError chain, no login redirects, no global error page.
 * - CapabilityUnavailableError: silent here — the component that owns the
 *   gated feature renders its own "not available yet" state.
 * - SessionExpiredError: silent here — the SessionExpiredGate latch owns it.
 * - Everything else: one antd notification; the UI stays up.
 */
function shouldStaySilent(error: unknown, meta?: Record<string, unknown>): boolean {
  if (meta?.silent) return true;
  return (
    error instanceof CapabilityUnavailableError ||
    error instanceof SessionExpiredError
  );
}

function notifyError(error: unknown): void {
  const message =
    error instanceof Error ? error.message : "Something went wrong.";
  notification.error({ message });
}

export function createStudioQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (shouldStaySilent(error, query.meta as Record<string, unknown>)) return;
        notifyError(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        if (shouldStaySilent(error, mutation.meta as Record<string, unknown>)) return;
        notifyError(error);
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error: unknown) => {
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if (status === 401 || status === 403 || status === 404 || status === 440) {
            return false;
          }
          return failureCount < 1;
        },
      },
    },
  });
}
